import { useEffect, useMemo, useState } from 'react';

import {
  faAddressCard,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  CheckboxDropdown,
  SingleSelectDropdown,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/videa-components';
import { endOfDay, isValid, startOfDay } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { uniqBy } from 'lodash';
import { useInView } from 'react-intersection-observer';

import { useGetZuubInsuranceList } from '@/react-query/useGetZuubInsuranceList';
import { usePreferences } from '@/react-query/usePreferences';

import { SummaryCard } from '../../../common-components/elements/SummaryCard/SummaryCard';
import type { Appointment } from '../../../react-query/useAppointments';
import { useAppointments } from '../../../react-query/useAppointments';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import { useSearchParams } from '../../../utils/hooks/useSearchParams';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { toBusinessName } from '../../../utils/string';
import { searchAtom, selectDateAtom } from '../../patientList/atoms';
import { PatientListPageLayout } from '../../patientList/PatientListPageLayout';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import { selectedCardAtom, selectedPayerFiltersAtom } from '../atoms';
import {
  calculateSummaryMetricsFromRows,
  filterRowsByCard,
  filterRowsByPayer,
  getUniquePayersFromRows,
} from '../helpers';
import type { AutoVerifyTableRow } from '../helpers';

import { RunVerificationTab } from '../runVerification/RunVerificationTab';

import { AutoVerifyTable } from './AutoVerifyTable';

const AUTO_VERIFY_TABS_TRIGGER_CLASSNAME =
  'vai-px-6 vai-pt-2 vai-pb-2 !vai-text-base vai-font-medium data-[state=active]:vai-bg-transparent vai-border-b-2 vai-border-transparent data-[state=active]:vai-border-blue-600 !vai-rounded-none !vai-shadow-none vai-text-gray-600 data-[state=active]:vai-text-blue-600 vai-transition-colors';

export const AutoVerifyPage = () => {
  // state
  const [activeTab, setActiveTab] = useState<'schedule' | 'run-verification'>(
    'schedule',
  );
  const search = useAtomValue(searchAtom);
  const { trackEvent } = useTrackEvent();
  const [selectedDate, selectDate] = useAtom(selectDateAtom);
  const [selectedCard, setSelectedCard] = useAtom(selectedCardAtom);
  const [selectedPayerFilters, setSelectedPayerFilters] = useAtom(
    selectedPayerFiltersAtom,
  );

  // hooks
  const { data: preferences } = usePreferences();
  const newPatientVerificationEnabled =
    preferences?.newPatientVerificationEnabled ?? false;
  const { t } = useLingui();
  const searchParams = useSearchParams();
  const { debouncedValue: debouncedSearch } = useDebounce(search);
  const { ref: bottomAppointmentRef, inView: isBottomAppointmentInView } =
    useInView();

  const isSearchValid = debouncedSearch.length > 2;

  const today = useMemo(() => new Date(), []);

  // Always have a default date range to ensure we get data
  let endDate: Date | undefined = endOfDay(today);
  let startDate: Date | undefined = startOfDay(today);
  let imagingStartDate: Date | undefined;
  let imagingEndDate: Date | undefined;

  if (selectedDate) {
    startDate = startOfDay(selectedDate);
    endDate = endOfDay(selectedDate);
  }

  // Clear date filters when searching to allow search across all dates
  if (isSearchValid) {
    endDate = undefined;
    startDate = undefined;
  }

  const appointmentArgs = {
    appointmentOpts: {
      startDate,
      endDate,
      imagingStartDate,
      imagingEndDate,
      onlyImaging: false,
    },
    patientOpts: {
      name: isSearchValid ? debouncedSearch : undefined,
    },
    meta: {
      sort: {
        appointmentOn: 'ASC' as const,
      },
    },
  };

  const {
    data: appointmentData,
    isLoading: isLoadingAppointments,
    isFetchingNextPage,
    fetchNextPage,
  } = useAppointments(appointmentArgs);

  // Same as what Huddle uses
  const appointments = useMemo(() => {
    if (!appointmentData?.pages) return [];
    const allAppointments = appointmentData.pages.flatMap((page) => page.data);
    const uniqueAppointments = uniqBy(allAppointments, 'appointment.unifiedId');
    return uniqueAppointments;
  }, [appointmentData?.pages]);

  // Extract unified IDs from appointments for Zuub API call
  const unifiedIds = useMemo(() => {
    return appointments.map((appointment) => appointment.appointment.unifiedId);
  }, [appointments]);

  // Fetch Zuub insurance data for all appointments
  const { data: zuubData } = useGetZuubInsuranceList({
    unifiedIds,
    enabled: unifiedIds.length > 0,
  });

  // Create a map of unifiedAppointmentId to Appointment for quick lookup
  const appointmentMap = useMemo(() => {
    const map = new Map<string, Appointment>();
    appointments.forEach((appointment) => {
      map.set(appointment.appointment.unifiedId, appointment);
    });
    return map;
  }, [appointments]);

  // Create rows from zuubData - group plans by unifiedAppointmentId (one row per appointment)
  const allRows = useMemo((): AutoVerifyTableRow[] => {
    if (!zuubData?.patients) return [];

    // Group plans by unifiedAppointmentId
    const rowsMap = new Map<string, AutoVerifyTableRow>();

    zuubData.patients.forEach((zuubPatient) => {
      (zuubPatient.plans || []).forEach((planData) => {
        const key = planData.unifiedAppointmentId;
        const existingRow = rowsMap.get(key);

        if (existingRow) {
          // Add plan to existing row
          existingRow.plans.push(planData);
        } else {
          // Create new row
          rowsMap.set(key, {
            plans: [planData],
            appointment:
              appointmentMap.get(planData.unifiedAppointmentId) || null,
            zuubPatient,
          });
        }
      });
    });

    // Sort plans by order (0 = primary, 1 = secondary, etc.) for each row
    const rows = Array.from(rowsMap.values());
    rows.forEach((row) => {
      row.plans.sort((a, b) => a.order - b.order);
    });

    return rows;
  }, [zuubData, appointmentMap]);

  // Apply payer filter
  const filteredRows = useMemo(() => {
    return filterRowsByPayer(allRows, selectedPayerFilters);
  }, [allRows, selectedPayerFilters]);

  // Get unique payers for filter dropdown
  const uniquePayers = useMemo(
    () => getUniquePayersFromRows(allRows),
    [allRows],
  );

  // Insurance status options for the single-select dropdown (matching card categories)
  const insuranceStatusOptions: Array<{
    value: 'all' | 'active' | 'noCoverage' | 'confirmDetails' | 'checkManually';
    label: string;
  }> = [
    { value: 'all', label: t`All` },
    { value: 'active', label: t`Active` },
    { value: 'noCoverage', label: t`No Coverage` },
    { value: 'confirmDetails', label: t`Confirm Details` },
    { value: 'checkManually', label: t`Check Manually` },
  ];

  // Calculate summary metrics and filter rows for table display
  const { summaryMetrics, filteredRowsForTable } = useMemo(
    () => ({
      summaryMetrics: calculateSummaryMetricsFromRows(filteredRows),
      filteredRowsForTable: filterRowsByCard(filteredRows, selectedCard),
    }),
    [filteredRows, selectedCard],
  );

  const { nextCursor } =
    appointmentData?.pages[appointmentData.pages.length - 1] || {};

  // useEffects
  useEffect(() => {
    const selectedDateParam = searchParams.get('selectedDateParam');

    if (selectedDateParam && isValid(new Date(selectedDateParam))) {
      selectDate(new Date(selectedDateParam));
      return;
    }

    // Only set to today if there's no selected date in global state AND no active search
    // This prevents the date from resetting when user is searching
    if (!selectedDate && !search) {
      selectDate(today);
    }
  }, [selectDate, today, searchParams, selectedDate, search]);

  // Auto-return to today when search is cleared and no date is selected
  // But only if the user isn't actively searching
  useEffect(() => {
    if (!search && !selectedDate) {
      selectDate(today);
    }
  }, [search, selectedDate, selectDate, today]);

  // Track page view
  useEffect(() => {
    trackEvent(mixpanelEvents.autoVerifyPageView);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !!appointmentData &&
      appointmentData.pages.length > 0 &&
      isBottomAppointmentInView &&
      nextCursor &&
      appointmentData?.pages.length &&
      appointmentData?.pages.length <= 20
    ) {
      fetchNextPage();
    }
  }, [
    appointmentData,
    isBottomAppointmentInView,
    nextCursor,
    appointmentData?.pages.length,
    fetchNextPage,
  ]);

  // Get unique appointments from filtered rows for the layout
  const filteredAppointments = useMemo(() => {
    const uniqueAppointments = new Map<string, Appointment>();
    filteredRows.forEach((row) => {
      if (row.appointment) {
        uniqueAppointments.set(
          row.appointment.appointment.unifiedId,
          row.appointment,
        );
      }
    });
    return Array.from(uniqueAppointments.values());
  }, [filteredRows]);

  const scheduleContent = (
    <div className="vai-flex vai-flex-col vai-h-full vai-overflow-hidden">
      {/* Summary Cards */}
      {!isLoadingAppointments && (
        <div className="vai-w-full vai-flex vai-gap-4 vai-mb-6 vai-justify-between vai-flex-shrink-0">
          {/* "All Patients" card */}
          <SummaryCard
            faIcon={faUsers}
            value={summaryMetrics.total}
            label={t`ALL PATIENTS`}
            description={
              summaryMetrics.total === 1
                ? t`patient scheduled for treatment today`
                : t`patients scheduled for treatment today`
            }
            color="blue"
            isSelected={selectedCard === 'all'}
            onClick={() => {
              trackEvent(mixpanelEvents.autoVerifySummaryCardClick, { card: 'all' });
              setSelectedCard('all');
            }}
            testId="auto-verify-summary-card-all-patients"
          />

          {/* "Active" card */}
          <SummaryCard
            faIcon={faCheckCircle}
            value={summaryMetrics.active}
            label={t`ACTIVE`}
            description={
              summaryMetrics.active === 1
                ? t`patient has active insurance coverage`
                : t`patients have active insurance coverage`
            }
            color="green"
            isSelected={selectedCard === 'active'}
            onClick={() => {
              trackEvent(mixpanelEvents.autoVerifySummaryCardClick, { card: 'active' });
              setSelectedCard('active');
            }}
            testId="auto-verify-summary-card-active"
          />

          {/* "No Coverage" card */}
          <SummaryCard
            faIcon={faTimesCircle}
            value={summaryMetrics.noCoverage}
            label={t`NO COVERAGE`}
            description={
              summaryMetrics.noCoverage === 1
                ? t`patient does not have active coverage`
                : t`patients do not have active coverage`
            }
            color="red"
            isSelected={selectedCard === 'noCoverage'}
            onClick={() => {
              trackEvent(mixpanelEvents.autoVerifySummaryCardClick, { card: 'noCoverage' });
              setSelectedCard('noCoverage');
            }}
            testId="auto-verify-summary-card-no-coverage"
          />

          {/* "Confirm Details" card */}
          <SummaryCard
            faIcon={faExclamationTriangle}
            value={summaryMetrics.confirmDetails}
            label={t`CONFIRM DETAILS`}
            description={
              summaryMetrics.confirmDetails === 1
                ? t`check failed due to missing or inaccurate data`
                : t`checks failed due to missing or inaccurate data`
            }
            color="yellow"
            isSelected={selectedCard === 'confirmDetails'}
            onClick={() => {
              trackEvent(mixpanelEvents.autoVerifySummaryCardClick, { card: 'confirmDetails' });
              setSelectedCard('confirmDetails');
            }}
            testId="auto-verify-summary-card-confirm-details"
          />

          {/* "Check Manually" card */}
          <SummaryCard
            faIcon={faAddressCard}
            value={summaryMetrics.checkManually}
            label={t`CHECK MANUALLY`}
            description={
              summaryMetrics.checkManually === 1
                ? t`patient requires a manual check via portal or phone`
                : t`patients require a manual check via portal or phone`
            }
            color="grey"
            isSelected={selectedCard === 'checkManually'}
            onClick={() => {
              trackEvent(mixpanelEvents.autoVerifySummaryCardClick, { card: 'checkManually' });
              setSelectedCard('checkManually');
            }}
            testId="auto-verify-summary-card-check-manually"
          />
        </div>
      )}

      {/* Filter Dropdowns */}
      {!isLoadingAppointments && (
        <div className="vai-flex vai-gap-4 vai-mb-4 vai-flex-shrink-0">
          {/* Payer Filter */}
          <CheckboxDropdown
            items={uniquePayers}
            selectedItems={selectedPayerFilters}
            onChange={(payers) => {
              trackEvent(mixpanelEvents.autoVerifyPayerFilterChange, { selectedPayers: payers });
              setSelectedPayerFilters(payers);
            }}
            getItemLabel={(item) => toBusinessName(item)}
            getItemValue={(item) => item}
            placeholder={t`Payer`}
            includeAllOption
            buttonClassName="vai-min-w-[280px]"
          />

          {/* Insurance Status Filter */}
          <SingleSelectDropdown
            items={insuranceStatusOptions}
            selectedItem={
              insuranceStatusOptions.find(
                (opt) => opt.value === selectedCard,
              ) || insuranceStatusOptions[0]
            }
            onChange={(item) => {
              trackEvent(mixpanelEvents.autoVerifyStatusFilterChange, { status: item.value });
              setSelectedCard(item.value);
            }}
            getItemLabel={(item) => item.label}
            getButtonLabel={(item) => `${t`Insurance Status`}: ${item.label}`}
            getItemValue={(item) => item.value}
            placeholder={t`Insurance Status`}
            buttonClassName="vai-min-w-[200px]"
          />
        </div>
      )}

      <div className="vai-flex-1 vai-min-h-0 vai-overflow-hidden">
        {isLoadingAppointments ? (
          <div className="vai-flex vai-justify-center vai-items-center vai-h-full">
            <div className="vai-border-4 vai-border-gray-200 vai-border-t-blue-500 vai-rounded-full vai-w-10 vai-h-10 vai-animate-spin" />
          </div>
        ) : filteredRowsForTable.length === 0 ? (
          <div className="vai-w-full vai-text-center vai-p-8 vai-text-zinc-700">
            <h3 className="vai-text-lg vai-font-medium vai-mb-2">
              <Trans>No patients found</Trans>
            </h3>
            <p className="vai-text-sm vai-text-zinc-500">
              <Trans>
                There are no patients matching your current filter. Try
                selecting a different status or check back later.
              </Trans>
            </p>
          </div>
        ) : (
          <>
            <AutoVerifyTable
              rows={filteredRowsForTable}
              isSearchActive={isSearchValid}
            />
            <div ref={bottomAppointmentRef} />
            {isFetchingNextPage && filteredRowsForTable.length !== 0 && (
              <div className="vai-flex vai-justify-center vai-p-4">
                <div className="vai-border-4 vai-border-gray-200 vai-border-t-blue-500 vai-rounded-full vai-w-8 vai-h-8 vai-animate-spin" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <PatientListPageLayout
      context={PatientListPageLayoutContextMap.AutoVerify}
      allAppointments={filteredAppointments}
      isLoadingAppointments={isLoadingAppointments}
      isLoadingVisitSummaries={false}
    >
      {newPatientVerificationEnabled ? (
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setActiveTab(value as 'schedule' | 'run-verification');
            trackEvent(mixpanelEvents.autoVerifyTabClick, { tab: value });
          }}
          className={
            activeTab === 'run-verification'
              ? 'vai-flex vai-flex-1 vai-flex-col vai-min-h-0'
              : 'vai-flex vai-h-full vai-flex-col vai-overflow-hidden'
          }
        >
          <div
            className="vai-flex vai-min-h-[65px] vai-items-center vai-border-b vai-border-gray-200 vai-pb-0"
            style={{ flexShrink: 0 }}
          >
            <TabsList
              variant="underline"
              className="vai-bg-transparent vai-border-b vai-border-transparent vai-rounded-none vai-pb-0 vai-px-0 vai-h-auto vai-min-h-0"
            >
              <TabsTrigger
                value="schedule"
                variant="underline"
                className={AUTO_VERIFY_TABS_TRIGGER_CLASSNAME}
              >
                <Trans>Schedule</Trans>
              </TabsTrigger>
              <TabsTrigger
                value="run-verification"
                variant="underline"
                className={AUTO_VERIFY_TABS_TRIGGER_CLASSNAME}
              >
                <Trans>Run Verification</Trans>
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent
            value="schedule"
            variant="underline"
            className="vai-mt-0 data-[state=active]:vai-flex data-[state=active]:vai-flex-1 data-[state=active]:vai-min-h-0 data-[state=active]:vai-flex-col data-[state=active]:vai-overflow-hidden"
          >
            {scheduleContent}
          </TabsContent>
          <TabsContent
            value="run-verification"
            variant="underline"
            className="vai-mt-0 data-[state=active]:vai-flex data-[state=active]:vai-flex-1 data-[state=active]:vai-min-h-0 data-[state=active]:vai-w-full data-[state=active]:vai-overflow-hidden"
          >
            <RunVerificationTab />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="vai-flex vai-h-full vai-flex-col vai-overflow-hidden">
          {scheduleContent}
        </div>
      )}
    </PatientListPageLayout>
  );
};
